import { useMemo } from "react"
import { GetApp } from "../../App"
import { GetCruise, GetShip, GetShipContext, GetTrip, DayContext } from "../../App/Context"
import { AppViewData } from "../App/Context"
import { useRefCallbacks } from "../../App/useRefCallbacks"
import { liveRecord } from "../../App/EditHistory"
import { classNames } from "../../Components"
import { Icon, Icons } from "../../Components/Icon"
import { TabBar, TabBarCallbacks } from "../../Components/TabBar"
import { INoteParentType } from "../../Types"
import { DayEditView } from "./EditView"
import { DayHeaderView } from "./HeaderView"
import { DayLogsTimelineView } from "./LogsTimelineView"
import { DayWatchView } from "./WatchView"
import { GraphView } from "../GraphView"
import { NoteTabs } from "../NoteView"
import { StatsView } from "../StatsView"
import { TrackView } from "../TrackView"
import { BlockRow } from "../../Components/Block"
import { PrintMode } from "../PrintView"
import { GetShipView } from "../ShipView"
import { DayViewData, dayToDate } from "./Context"
import { useDayData } from "./DataContext"
import type { DayDataProps } from "./DayView"

// Purely presentational -- all fetching/subscribing lives in the mounted
// `DayDataView*` acquisition variant (see `DayView.tsx`); this component only reads
// the acquired data via `useDayData()` and owns tab-bar wiring plus the descendant
// `DayContext` it provides to its own subtree (distinct from the acquisition
// variant's `DayDataContext`).
export function DayViewUI({ advices, currentLog, currentLogView, day, dayChildren, focus, hideTimeline, initLogTabs, initTab, isCurrentDay, isTomorrow, isYesterday, isFirstTripDay, previousDay }: DayDataProps) {
  const app = GetApp();
  const shipView = GetShipView();
  const [ship, cruise, trip] = [GetShip(), GetCruise().cruise, GetTrip()];
  const { apiUser } = GetShipContext();
  const t = app.getT("DayView");
  const tabBarRef = useRefCallbacks<TabBarCallbacks>();
  const isCurrentTrip = trip.trip.completedAt == null;
  const focusEditable = focus && AppViewData.availableCurrentOrAll(apiUser, ship.id, isCurrentTrip);
  const calculated = day.calculated;
  const {
    logs, statuses, tracks, anchorCircles, auditLogEdits, timelineLogs, recordAuditLogEdits, savedDay, recordSavedDay,
    details, setDetails, selected, toggleSelected, highlight, clearHighlight, selectedCallback, writers,
  } = useDayData();
  const renderedDay = liveRecord(day, savedDay);

  const showDetail = useMemo(() => {
    return DayViewData.showDetail(details, isCurrentDay || false, isTomorrow || false, selected, !day.id);
  }, [details, isCurrentDay, isTomorrow, selected, day.id]);
  const showTimeline = useMemo(() => {
    return DayViewData.showTimeline(showDetail, isCurrentDay || false, hideTimeline || false, !day.id);
  }, [showDetail, hideTimeline, isCurrentDay, day.id]);

  const date = dayToDate(day.day);
  const dayContext = {
    day: renderedDay,
    focus: focus || false,
    isCurrentTrip,
    logs: logs || [],
    statuses: statuses || [],
    tracks: tracks || [],
    auditLogEdits,
    recordAuditLogEdits,
    recordSavedDay,
    ...writers,
  };
  return (
    <DayContext.Provider value={dayContext}>
      <div className={classNames(["DayView", selected ? "selected" : "not-selected", !day.id && !selected && "blank", details && "details", isCurrentDay && "current", isTomorrow && "tomorrow", DayViewData.isWeekend(date) && "highlight-weekend", DayViewData.monthHighlight(date) && "highlight-month"])} data-day-id={day.id}>
        <DayHeaderView advices={advices} currentLog={currentLog} details={details} isCurrentDay={isCurrentDay || false} isTomorrow={isTomorrow || false} isYesterday={isYesterday || false} logs={timelineLogs} selected={selected} setDetails={setDetails} previousDay={previousDay} toggleSelected={toggleSelected} />
        {selected && (
          <>
            {showDetail && (
              <div className="DayView-content">
                <TabBar defaultSelection={initTab || "day"} callbacks={tabBarRef} tabs={[
                  { icon: Icons.Day, name: "day", title: t("tab_day"), render: () => {
                    return <DayEditView day={renderedDay} editable={focusEditable} isCurrentDay={isCurrentDay} isFirstTripDay={isFirstTripDay} isTomorrow={isTomorrow} previousDay={previousDay} />;
                  } },
                  { hide: !day.id || isTomorrow || !calculated, icon: Icons.Stats, name: "stats", title: t("tab_stats"), render: () => {
                    return <StatsView day={day} calculate={writers.calculateDay} />;
                  } },
                  { hide: !day.id || isTomorrow, icon: Icons.Track, name: "track", title: t("tab_track"), render: () => {
                    return (
                      <div className="DayView-track">
                        <TrackView fullscreen={false} tracks={tracks} anchorCircles={anchorCircles} timezone={day.timezone} />
                      </div>
                    );
                  } },
                  { hide: !day.id || isTomorrow || !calculated || !calculated.weatherTimeseries, icon: Icons.Graph, name: "weather", title: t("tab_weather"), render: () => {
                    return <GraphView timeseries={calculated?.weatherTimeseries} />;
                  } },
                  ...(
                    NoteTabs(app, INoteParentType.day, day.notes || [])
                  ),
                  { hide: !day.id, icon: Icons.Watch, name: "watch", title: t("tab_watches"), render: () => {
                    return <DayWatchView day={day} />;
                  } },

                  { hide: !day.id || isTomorrow, icon: Icons.Print, name: "print", title: t("tab_print"), render: () => {
                    return (
                      <div className="TripView-print-tab">
                        <BlockRow className="block-row-button-print">
                          <button className="button" onClick={() => {
                            shipView.setPrinting(PrintMode.days, cruise, trip.trip.id);
                          }}
                          >
                            <Icon icon={Icons.Print} className="icon-large" /> {t("print_days")}
                          </button>
                        </BlockRow>
                        <BlockRow className="block-row-button-print">
                          <button className="button" onClick={() => {
                            shipView.setPrinting(PrintMode.days, cruise, trip.trip.id, day.id);
                          }}
                          >
                            <Icon icon={Icons.Print} className="icon-large" /> {t("print_day")}
                          </button>
                        </BlockRow>
                      </div>
                    );
                  } },
                ]} selectedCallback={selectedCallback}
                />
              </div>
            )}
            {dayChildren}
            {showTimeline && <DayLogsTimelineView clearHighlight={clearHighlight} currentLog={currentLog} currentLogView={currentLogView} highlight={highlight} isCurrentDay={isCurrentDay} isTomorrow={isTomorrow} logTabs={initLogTabs} logs={timelineLogs} statuses={statuses || []} />}
          </>
        )}
      </div>
    </DayContext.Provider>
  );
}
